import React from 'react';
import PaymentStatus from 'constants/payment-status';
import { css } from 'styled-components';
import Order from './components/Order';
import Delivery from './components/Delivery';
import i18n from 'i18n';
import { dateFormat } from 'util/filters';
import { applyStyles, downloadFile } from 'util/helpers';
import CustomMedia from 'util/custom-media-query';
import MediaBorders from 'constants/media-borders';
import Button from 'components/common/Button';
import Colors from 'constants/colors';
import FontStyles from 'constants/font-styles';
import Breadcrumbs from 'components/common/Breadcrumbs';
import GrayHeader from 'components/common/GrayHeader';
import { zeroPadStart } from 'util/filters';
import PublishDialog from 'components/OrderAggregators/components/PublishDialog';

class History extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      receiptDownloading: false
    };
    this.publishDialogRef = React.createRef();
  }

  async componentDidMount() {
    try {
      await this.props.fetchHistory();
    } catch (error) {
      if (this.props.error) {
        const {
          showAlert,
          error: {
            data: { message }
          }
        } = this.props;

        // 画面遷移時にアラートが消えるのでpush後にアラートを表示する
        this.props.push('/mypage/login');
        return showAlert(message);
      }
    }

    this.setState({
      loaded: true
    });
  }

  componentDidUpdate(prevProps) {
    const { receiptURL } = this.props;

    if (prevProps.receiptURL == null && receiptURL) {
      downloadFile('receipt.pdf', receiptURL);
      this.setState({
        receiptDownloading: false
      });
    }
  }

  downloadReceipt = (value) => {
    if (
      navigator.userAgent.includes('iPad') ||
      navigator.userAgent.includes('iPhone')
    ) {
      return window.open(
        this.props.history.createHref({
          pathname: '/preview',
          search: `?target=receipt&projectId=${this.props.headerInfo.projectId}&name=${value.name}`
        })
      );
    }

    this.props.downloadReceipt(this.props.headerInfo.projectId, value.name).catch(() => {
      if (this.props.error) {
        const {
          showAlert,
          error: {
            data: { message }
          }
        } = this.props;
        return showAlert(message);
      }
    });

    this.setState({
      receiptDownloading: true
    });
  };

  render() {
    const { receiptDownloading } = this.state;
    const {
      className,
      location: { pathname }
    } = this.props;

    return (
      this.state.loaded && (
        <div className={className}>
          <GrayHeader text={i18n.t('mypage:grayHeaderText')} />
          <Breadcrumbs currentPath={pathname} />
          <div className="order-container">
            <Order
              {...this.props.orderInfo}
              paymentStatus={this.props.headerInfo.paymentStatus}
            />
            <div className="order-summary">
              <p>
                <span className="content-label">
                  {i18n.t('mypage:orderNumber')}
                </span>
                <span> : </span>
                {zeroPadStart(this.props.headerInfo.projectId)}
              </p>
              <p>
                <span className="content-label">
                  {i18n.t('mypage:orderDate')}
                </span>
                <span> : </span>
                {dateFormat(
                  this.props.headerInfo.orderOn,
                  i18n.t('common:dateFormat.dateName')
                )}
              </p>
              <div className="order-info-buttons">
                <Button
                  className="invoice"
                  type="button"
                  buttonType="B"
                  disabled={true}
                >
                  {i18n.t('mypage:invoicing')}
                </Button>
                <Button
                  type="button"
                  buttonType="B"
                  onClick={() => this.publishDialogRef.current.open()}
                  disabled={
                    receiptDownloading ||
                    this.props.headerInfo.paymentStatus !==
                      PaymentStatus.settled
                  }
                >
                  {i18n.t('mypage:receiptIssue')}
                </Button>
              </div>
              <PublishDialog
                dialogRef={this.publishDialogRef}
                onSubmit={v => this.downloadReceipt(v)}
                renderType={'receipt'}
              />
            </div>
          </div>

          <div className="delivery-container">
            <ul className="delivery-list">
              {this.props.deliveryInfo.map(delivery => (
                <li key={delivery.index}>
                  <Delivery {...delivery} opened={true} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      )
    );
  }
}

const commonStyle = css`
  .order-container {
    .order-summary {
      p {
        margin: 0 0 5px;
      }
    }
  }

  .delivery-container {
    .delivery-list {
      width: 100%;
      padding: 0;
      margin: 0;
      list-style: none;
    }
  }

  .invoice {
    display: none;
  }
`;

const pcStyle = CustomMedia.greaterThan('umedium')`
  ${FontStyles.text7};

  padding: 16px 160px 0px;
  margin: 104px auto 0;
  max-width: ${MediaBorders.large};

  .order-container {
    display: flex;
    width: 100%;
    margin-top: 40px;

    ${Order} {
      width: calc(100% - 340px);
    }

    .order-summary {
      position: relative;
      width: 340px;
      padding-left: 32px;
      border-left: 1px solid ${Colors.gray2};

      p {
        display: flex;
        justify-content: space-between;
        padding-left: 118px;
        margin-top: 0;

        .content-label {
          ${FontStyles.text8};
          margin-right: 6px;

          & + span {
            flex-grow: 1;
          }
        }
      }

      .order-info-buttons {
        position: absolute;
        bottom: 0;

        ${Button}:first-of-type {
          margin-bottom: 24px;
        }
      }
    }
  }

  .delivery-container {
    display: flex;
    margin-top: 40px;

    .delivery-list {
      > li {
        margin-bottom: 32px;

        &:last-child {
          margin-bottom: 160px;
        }
      }
    }
  }
`;

const spStyle = CustomMedia.lessThan('medium')`
  ${FontStyles.text5};

  width: 100%;
  padding: 8px 16px 0px;

  .order-container {
    margin-top: 28px;

    .order-summary {
      padding-top: 18px;
      border-top: 1px solid ${Colors.gray2};
      margin: 16px 0 32px;

      p {
        margin-bottom: 3px;
      }

      .content-label {
        display: inline-block;
        width: 66px;
        ${FontStyles.text6};
      }

      .order-info-buttons {
        ${Button} {
          margin: 21px 0 0;

          &:first-of-type {
            margin-bottom: 16px;
          }
        }
      }
    }
  }

  .delivery-container {
    .delivery-list {
      > li {
        margin-bottom: 38px;

        &:last-child {
          margin-bottom: 65px;
        }
      }
    }
  }
`;

export default applyStyles(History)(commonStyle, pcStyle, spStyle);



// WEBPACK FOOTER //
// ./src/components/Mypage/History/index.js